import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Separator } from '@/components/ui-kit/separator';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { showSuccessToast } from '@/hooks/use-toast';
import { PlatformIntegrationCard } from '../platform-integration-card';
import { Check, CodeXml, Copy } from 'lucide-react';
// import Link from 'next/link';
import QRCode from 'react-qr-code';
import { Link } from 'react-router-dom';

type AgentIntegrationChatbotScriptProps = {
  widgetId: string;
  projectKey: string;
  appDomainName: string;
};

const integrations = [
  {
    name: 'Slack',
    description:
      'Integrate your chatbot with Slack to deliver messages and notifications directly to your workspace.',
    imageUrl: '/assets/images/agents/slack.png',
    script: '<script>// Slack integration code...</script>',
  },
  {
    name: 'Teams',
    description:
      'Connect your chatbot to Microsoft Teams and engage with users across your team channels in real time.',
    imageUrl: '/assets/images/agents/business.png',
    script: '<script>// Teams integration code...</script>',
  },
  {
    name: 'Discord',
    description:
      'Enable your chatbot inside Discord servers to respond to users and automate community interactions.',
    imageUrl: '/assets/images/agents/discord.png',
    script: '<script>// Discord integration code...</script>',
  },
  {
    name: 'WhatsApp',
    description:
      'Reach users on WhatsApp with automated chatbot replies and two-way conversations.',
    imageUrl: '/assets/images/agents/whatsapp.png',
    script: '<script>// WhatsApp integration code...</script>',
  },
  {
    name: 'Telegram',
    description: 'Connect your chatbot with Telegram to handle chats, private messages',
    imageUrl: '/assets/images/agents/telegram.png',
    script: '<script>// Telegram integration code...</script>',
  },
];

export const AgentIntegrationChatbotScript = ({
  widgetId,
  projectKey,
  appDomainName,
}: AgentIntegrationChatbotScriptProps) => {
  const { t } = useTranslation();
  const { isCopying, copy } = useCopyToClipboard();
  const scriptContent = ` 
  <script
  src="${import.meta.env.VITE_WIDGET_URL}/embed.js"
  data-widget-id="${widgetId}"
  data-widget-type="chat"
  data-project-key="${projectKey}"
  data-app-domain="${appDomainName}"
  data-app-mode="${import.meta.env.VITE_APP_ENV}"
  >
</script>`;

  const standaloneUrl = `${import.meta.env.VITE_WIDGET_URL}/widget/standalone?widget_id=${widgetId}&project_key=${projectKey}&app-mode=${import.meta.env.VITE_APP_ENV}`;

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 border-high-emphasis font-medium">
          <CodeXml className="aspect-square w-5" /> {t('CHATBOT_EMBED_SCRIPT')}
        </div>
        <Button
          variant="ghost"
          className="h-full w-fit p-0"
          type="button"
          disabled={isCopying}
          onClick={() =>
            copy(scriptContent, () => {
              showSuccessToast({ description: t('CODE_COPIED') });
            })
          }
        >
          {isCopying ? (
            <Check className="aspect-square w-4" />
          ) : (
            <Copy className="aspect-square w-4" />
          )}
        </Button>
      </div>
      <Card>
        <CardContent className="break-words">{scriptContent}</CardContent>
      </Card>
      <div>
        <div className="text-base">
          <h3 className="border-high-emphasis font-medium">{t('FULL_CHAT_EXPERIENCE')}</h3>
          <p className="mt-3 font-normal text-high-emphasis">
            {t('FULL_CHAT_EXPERIENCE_DESC')}
          </p>
        </div>
        <div className="mt-4 flex items-center gap-1">
          <Input value={standaloneUrl} readOnly />
          <Link to={standaloneUrl} target="_blank">
            <Button type="button">{t('OPEN')}</Button>
          </Link>
        </div>
        <div className="my-3 flex w-full items-center gap-2 border-low-emphasis">
          <Separator className="flex-1" />
          <span className="text-xs font-medium text-low-emphasis">{t('OR')}</span>
          <Separator className="flex-1" />
        </div>
        <div>
          <p className="text-base font-normal text-high-emphasis">{t('SCAN_THE_CODE')}</p>
          <div className="mt-4 flex items-center justify-center">
            <QRCode
              style={{ height: 'auto', maxWidth: '100%', width: '233px' }}
              value={standaloneUrl}
            />
          </div>
        </div>
      </div>
      <div className="mt-2 flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <h3 className="font-medium">{t('OTHER_INTEGRATIONS')}</h3>
          <Badge variant="info">{t('COMING_SOON')}</Badge>
        </div>
        <div className="grid gap-6">
          {integrations.map((item) => (
            <PlatformIntegrationCard key={item.name} {...item} description={t(`${item.name.toUpperCase()}_INTEGRATION_DESC`)} />
          ))}
        </div>
      </div>
    </div>
  );
};
